/**
 * The conversation on a ticket, and what happened to it.
 *
 * Both are the same list, because both are the ticket's history: a message
 * someone typed sits in a bubble, a change the service recorded sits as a line
 * down the middle.
 *
 * Legacy kept this on a websocket per ticket and wrote the second kind into it
 * from the browser after each save — so a change made anywhere else never
 * appeared at all, and a dropped socket lost the lot. Here the chat is read
 * from the ticket and re-read every twenty seconds while the tab is open. A
 * conversation on a help desk moves in minutes; a socket per ticket is a great
 * deal of machinery for that, and the cost of not having one is that a
 * colleague's reply can be twenty seconds old.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Send, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { AttachmentList, AttachmentPicker } from "@/components/helpdesk/attachments";
import { Button, IconButton } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Form, Textarea } from "@/components/ui/field";
import { SkeletonLines } from "@/components/ui/skeleton";
import { api, errorMessage } from "@/services/api";
import {
  type AttachmentDraft,
  type Comment,
  commentsQuery,
  HELPDESK_KEY,
  TICKETS_ENDPOINT,
  isClosed,
  type Ticket,
  toAttachmentInput,
  uploadDrafts,
} from "@/services/helpdesk";
import { profileQuery } from "@/services/queries";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

export function TicketThread({ ticket }: { ticket: Ticket }) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const allow = useCan();
  const { data: profile } = useQuery(profileQuery());
  const thread = useQuery(commentsQuery(ticket.id));
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<AttachmentDraft[]>([]);
  const [removing, setRemoving] = useState<Comment | null>(null);
  const foot = useRef<HTMLDivElement>(null);

  const lines = thread.data ?? [];
  const closed = isClosed(ticket.status);
  const canWrite = allow("add__helpdesk_comment") && !closed;
  const moderates = allow("delete__helpdesk_comment");

  // Follow the conversation down as it grows. Keyed on the number of lines
  // rather than the array, so a re-read that changed nothing does not yank the
  // view away from somebody reading back through it.
  useEffect(() => {
    // Optional-called, as every other scroller here is: jsdom ships no layout
    // to scroll, and a thread that throws on mount under test is a thread
    // nothing can be asserted about.
    foot.current?.scrollIntoView?.({ block: "end" });
  }, [lines.length]);

  const send = useMutation({
    mutationFn: async () =>
      api.post<Comment>(`/helpdesk/tickets/${ticket.id}/comments`, {
        // Sent as it was typed, empty included: a screenshot on its own is a
        // perfectly good thing to say, and a line of filler above the picture
        // was words nobody wrote.
        body: body.trim(),
        // Stored now rather than when they were picked, so a message begun and
        // abandoned leaves nothing behind.
        attachments: toAttachmentInput(await uploadDrafts(files)),
      }),
    onSuccess: async () => {
      setBody("");
      setFiles([]);
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
    },
    onError: (failure) => toast.error(errorMessage(failure, t("hd.sendFailed"))),
  });

  const remove = useMutation({
    mutationFn: async (line: Comment) =>
      api.delete(`/helpdesk/tickets/${ticket.id}/comments/${line.id}`),
    onSuccess: async () => {
      toast.success(t("hd.messageRemoved"));
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
    },
    onError: (failure) => toast.error(errorMessage(failure, t("hd.removeFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (body.trim().length === 0 && files.length === 0) return;
    send.mutate();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {thread.isLoading ? (
          <SkeletonLines rows={5} />
        ) : lines.length === 0 ? (
          <EmptyState
            icon={MessageCircle}
            title={t("hd.noMessagesTitle")}
            description={t("hd.noMessagesBody")}
          />
        ) : (
          <ol className="flex flex-col gap-3">
            {lines.map((line) =>
              line.kind === "message" ? (
                <Message
                  key={line.id}
                  line={line}
                  mine={line.created_by_id === profile?.id}
                  onRemove={
                    line.created_by_id === profile?.id || moderates
                      ? () => setRemoving(line)
                      : undefined
                  }
                />
              ) : (
                <Event key={line.id} line={line} />
              ),
            )}
          </ol>
        )}
        <div ref={foot} />
      </div>

      {closed ? (
        <p className="bg-surface-inset text-fg-muted text-subhead rounded-card px-3.5 py-2.5">
          {t("hd.closedThread")}
        </p>
      ) : canWrite ? (
        <Form onSubmit={submit} className="flex flex-col gap-2">
          <Textarea
            rows={2}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={t("hd.messagePlaceholder")}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter is a new line — what every chat box in
              // the world does, and what legacy's editor did.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit(event);
              }
            }}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AttachmentPicker
              id="comment-attachments"
              value={files}
              onChange={setFiles}
              disabled={send.isPending}
            />
            <Button
              type="submit"
              loading={send.isPending}
              loadingLabel={t("hd.sending")}
              disabled={body.trim().length === 0 && files.length === 0}
            >
              <Send className="size-4" aria-hidden />
              {t("hd.send")}
            </Button>
          </div>
        </Form>
      ) : null}

      {removing ? (
        <ConfirmDialog
          open
          onClose={() => setRemoving(null)}
          onConfirm={() => remove.mutate(removing)}
          tone="danger"
          title={t("hd.removeMessage")}
          message={t("hd.removeMessageBody")}
          confirmLabel={t("common.delete")}
          loading={remove.isPending}
        />
      ) : null}
    </div>
  );
}

/** When it was said, in words — "3 minutes ago" is what a thread is read by. */
function when(value: string) {
  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

function Message({
  line,
  mine,
  onRemove,
}: {
  line: Comment;
  mine: boolean;
  onRemove?: (() => void) | undefined;
}) {
  const t = useTranslation();
  return (
    <li className={cn("flex", mine ? "justify-end" : "justify-start")}>
      <div className="flex max-w-[80%] flex-col gap-1.5">
        <div className="text-fg-subtle text-footnote flex items-center gap-2">
          <span className="font-medium">
            {mine ? t("hd.you") : (line.created_by_name ?? "—")}
          </span>
          <span>{when(line.created_at)}</span>
          {onRemove ? (
            <IconButton label={t("hd.removeMessage")} size="icon-sm" onClick={onRemove}>
              <Trash2 aria-hidden />
            </IconButton>
          ) : null}
        </div>
        {/* A line with no words is its files, and nothing else — no empty
            bubble above them. */}
        {line.body ? (
          <div
            className={cn(
              "rounded-card text-body px-3.5 py-2.5 whitespace-pre-wrap",
              mine ? "bg-accent-soft text-fg" : "bg-surface-inset text-fg",
            )}
          >
            {line.body}
          </div>
        ) : null}
        <AttachmentList files={line.attachments} className={mine ? "justify-end" : undefined} />
      </div>
    </li>
  );
}

/** A change the service recorded: centred, quiet, and not addressed to anyone. */
function Event({ line }: { line: Comment }) {
  return (
    <li className="flex items-center gap-3">
      <span className="bg-line h-px flex-1" aria-hidden />
      <span className="text-fg-subtle text-footnote text-center">
        {line.body} · {when(line.created_at)}
      </span>
      <span className="bg-line h-px flex-1" aria-hidden />
    </li>
  );
}
